import {NetworkError} from './Error.mjs'
import deepEqual from './deepEqual.mjs'

const doFetch = async (resource, init) => {
    const response = await window.fetch(resource, init)

    if (response.ok) {
        return response
    }
    else {
        const {status, statusText} = response
        throw new NetworkError(status, statusText)
    }
}

const fetch = async (resource, init) => {
    const response = await doFetch(resource, init)
    const contentType = response.headers.get('Content-Type')

    if (/^application\/json/.test(contentType)) {
        return response.json()
    }
    else {
        const blob = await response.blob()

        const fileName = (() => {
            const cd = response.headers.get('Content-Disposition')

            if (cd) {
                const match = cd.match(/filename=(\S+)/)

                if (match) {
                    return window.decodeURIComponent(match[1])
                }
            }

            return ''
        })()

        return {blob, fileName}
    }
}

const fetchStream = async function* (resource, init) {
    const response = await doFetch(resource, init)
    const contentType = response.headers.get('Content-Type')

    if (/^text\/plain/.test(contentType)) {
        const reader = response.body.getReader()
        const dec = new TextDecoder()

        while (true) {
            const {done, value} = await reader.read()

            if (done) {
                break
            }

            yield dec.decode(value, {stream: true})
        }
    }
    else if (/^application\/json/.test(contentType)) {
        yield response.json()
    }
    else {
        // TODO
    }
}

const requestPool = (() => {
    const pool = new Map()

    return {
        delete: (promise) => {
            pool.delete(promise)
        },

        get: (request) => {
            for (const [promise, req] of pool.entries()) {
                if (deepEqual(req, request)) {
                    return promise
                }
            }
        },

        set: (request, promise) => {
            pool.set(promise, request)
        },
    }
})()

const doRequest = ({
    headers,
    method,
    payload,
    signal,
    streaming,
    url,
}) => {
    const resource = new window.URL(url)

    const init = {
        headers: new Headers({
            'Accept': 'application/json;charset=UTF-8',
            'Accept-Encoding':'gzip,deflate',

            ...Object.fromEntries(
                Object.entries(headers).filter(
                    ([k, v]) => undefined !== v && null !== v
                )
            )
        }),

        method,
        signal,
    }

    if (! init.headers.has('content-type')) {
        init.headers.set('content-type', 'application/json;charset=UTF-8')
    }

    const contentType = init.headers.get('content-type')

    if ('GET' === method || 'HEAD' === method) {
        for (const [key, value] of Object.entries(payload)) {
            if (undefined !== value && null !== value) {
                if (Array.isArray(value)) {
                    for (var v of value) {
                        resource.searchParams.append(key, v)
                    }
                }
                else {
                    resource.searchParams.set(key, value)
                }
            }
        }
    }
    else if (contentType.startsWith('multipart/form-data')) {
        init.headers.delete('content-type')
        const body = new FormData()

        for (const [key, value] of Object.entries(payload)) {
            if (Array.isArray(value)) {
                for (const e of value) {
                    body.append(key, e)
                }
            }
            else {
                body.append(key, value)
            }
        }

        init.body = body
    }
    else if (contentType.startsWith('application/json')) {
        if (payload && 'string' !== typeof payload) {
            init.body = JSON.stringify(payload)
        }
        else {
            init.body = payload
        }
    }
    else {
        init.body = payload
    }

    return streaming ?
        fetchStream(resource, init)
        :
        fetch(resource, init)
}

export default ({
    headers = {},
    method,
    payload = {},
    streaming = false,
    signal,
    url,
}) => {
    const tuple = {headers, method, payload, streaming, url}
    const reusablePromise = requestPool.get(tuple)

    if (reusablePromise) {
        return reusablePromise
    }

    const promise = doRequest({
        headers,
        method,
        payload,
        signal,
        streaming,
        url,
    })

    requestPool.set(tuple, promise)

    ;(async () => {
        try {
            await promise
        }
        finally {
            requestPool.delete(promise)
        }
    })()

    return promise
}
